import { BonusPoints } from '../basket-checkout-common/BonusPoints.js';
import { BonusPointsSheetTracking } from './BonusPointsSheetTracking.js';
import { BonusPointsSheetState, BonusPointsSheetTileState, BonusPointsSheetTileStatus } from './BonusPointsSheetState.js';
import { sheetV1 } from '@otto-ec/otto-components/sheet';

export class BonusPointsSheet {
    private readonly _sheetTracking: BonusPointsSheetTracking;
    private readonly _bonusPoints: BonusPoints;

    _selectedBonusPoints: string;

    private _closedByRedeem: boolean;
    private _sheetState?: BonusPointsSheetState;

    constructor(sheetTracking: BonusPointsSheetTracking, bonusPoints: BonusPoints) {
        this._sheetTracking = sheetTracking;
        this._bonusPoints = bonusPoints;

        this._selectedBonusPoints = '0';
        this._closedByRedeem = false;
    }

    initialize() {
        return [...this._subscribeToContentLoadedEvent(), ...this._subscribeToClosedEvent()];
    }

    private _initializeOpenedSheet() {
        this._initializeSelectedBonusPoints();
        this._initializeElements();
        this._initializeSheetState();

        this._sheetTracking.trackOpenedSheet(this._currentSheetState);
    }

    get _currentSheetState(): BonusPointsSheetState {
        if (this._sheetState === undefined) {
            throw new Error('Could not find bonus points sheet state');
        }

        return this._sheetState;
    }

    private _initializeSelectedBonusPoints() {
        for (const element of Array.from(document.querySelectorAll<HTMLInputElement>('oc-selection-tile-v2 input[name=selectedBonusPoints]'))) {
            if (element.checked) {
                this._selectedBonusPoints = element?.value || '0';
                break;
            }
        }
    }

    private _initializeElements() {
        const cancelButton = document.getElementById('or_js_bonusPointsSheet__cancelButton');
        const submitButton = document.getElementById('or_js_bonusPointsSheet__submitButton');

        if (cancelButton) {
            this._initializeClickListener(cancelButton, () => sheetV1.current.get().instance?.close());
        }
        if (submitButton) {
            this._initializeClickListener(submitButton, () => this._redeemSelectedBonusPoints());
        }

        const selectionTileElements = Array.from(document.querySelectorAll<HTMLInputElement>('oc-selection-tile-v2 input[name=selectedBonusPoints]'));
        for (const selectionTileElement of selectionTileElements) {
            this._initializeClickListener(selectionTileElement, () => {
                this._checkInput(selectionTileElement, selectionTileElements[0]);
            });
        }
    }

    private _initializeClickListener(element: HTMLElement, listener: () => void) {
        if (element.dataset.initialized !== 'true') {
            element.addEventListener('click', listener);
            element.addEventListener('keypress', event => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    listener();
                }
            });
            element.dataset.initialized = 'true';
        }
    }

    private async _checkInput(element: HTMLInputElement, firstElement: HTMLInputElement) {
        /*                                                                            */
        await new Promise(resolve => setTimeout(resolve, 0));
        if (this._selectedBonusPoints === element.value) {
            this._selectedBonusPoints = '0';
            firstElement.focus();
            element.checked = false;
        } else {
            if (element.value) {
                this._selectedBonusPoints = element.value;
            }
        }

        this._updateTileState();
    }

    private _subscribeToContentLoadedEvent() {
        const unsubscribeContentApply: () => void = sheetV1.events.contentApply.subscribe(e => {
            if (e.instance.id === 'or_bonusPointsSheet') {
                this._initializeOpenedSheet();
            }
        });
        return [unsubscribeContentApply];
    }

    private _subscribeToClosedEvent() {
        const unsubscribeSheetClose: () => void = sheetV1.events.close.subscribe(e => {
            if (e.id === 'or_bonusPointsSheet') {
                if (this._closedByRedeem) {
                    this._sheetTracking.trackSubmittedSheet(this._currentSheetState);
                    this._closedByRedeem = false;
                } else {
                    this._sheetTracking.trackClosedSheet(this._currentSheetState);
                }
            }
        });
        return [unsubscribeSheetClose];
    }

    private _initializeSheetState() {
        const tileElements = this._tiles;
        const selectedTile = tileElements.find(element => element.checked);
        const redeemedPoints = selectedTile?.value ?? '0';
        const redeemedBalance = selectedTile?.dataset.redeemedBalance ?? '0.00';
        const messageBoxReason = document.getElementById('or_js_bonusPointsSheet__hintBanner')?.dataset.reason;
        const tiles = this._createTilesState(tileElements);

        this._sheetState = {
            redeemedPointsBeforeSubmit: redeemedPoints,
            redeemedBalanceBeforeSubmit: redeemedBalance,
            messageBoxReason,
            tiles
        };
    }

    private get _tiles(): HTMLInputElement[] {
        return Array.from(document.querySelectorAll('oc-selection-tile-v2 input[name=selectedBonusPoints]'));
    }

    private _updateTileState() {
        this._sheetState = {
            ...this._currentSheetState,
            tiles: this._createTilesState(this._tiles)
        };
    }

    private _createTilesState(tiles: HTMLInputElement[]): BonusPointsSheetTileState[] {
        return tiles.map(
            tile =>
                ({
                    status: this._getTileStatus(tile),
                    points: tile.value,
                    balance: tile.dataset.redeemedBalance
                }) as BonusPointsSheetTileState
        );
    }

    private _getTileStatus(tile: HTMLInputElement): BonusPointsSheetTileStatus {
        if (tile.disabled) {
            return 'disabled';
        }

        if (tile.checked) {
            return 'selected';
        }

        return 'visible';
    }

    _redeemSelectedBonusPoints() {
        return this._bonusPoints
            .redeemBonusPointsAndReload(this._selectedBonusPoints)
            .then(() => this._closeSheetByRedeem())
            .catch(() => this._handleRedemptionError());
    }

    private _closeSheetByRedeem() {
        this._closedByRedeem = true;
        sheetV1.current.get().instance?.close();
    }

    private _handleRedemptionError() {
        this._bonusPoints.renderBonusPointRedemptionError();
        this._sheetTracking.trackError(this._currentSheetState, BonusPoints.REDEMPTION_ERROR_MESSAGE_ID + '.sheet');
    }
}
